import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CheckCircle2, DollarSign, Award } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatIDR } from "@/lib/format";
import { PageHeader, Card, Loading, Button, EmptyState, Modal, Field } from "@/components/kit";

const rowKey = (r) => `${r.member_id}-${r.class_name}-${r.date}-${r.time}`;

export default function PendingPayments() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payFor, setPayFor] = useState(null);
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [msBusy, setMsBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await api.classPendingPayments()); } catch (e) { toast.error(e.message); setRows([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmMembership = async (r) => {
    setMsBusy(rowKey(r));
    try {
      await api.classConfirmMembership({ member_id: r.member_id, class_name: r.class_name, date: r.date, time: r.time, category: r.category });
      toast.success(`${r.member_name} · join via Membership`);
      await load();
    } catch (e) { toast.error(e.message || "Gagal konfirmasi membership"); }
    finally { setMsBusy(null); }
  };

  const submit = async () => {
    if (!payFor) return;
    const amt = parseInt(String(amount).replace(/[^0-9]/g, ""), 10) || 0;
    if (amt <= 0) { toast.error("Masukkan nominal yang valid"); return; }
    setSaving(true);
    try {
      const res = await api.classMarkPaid({ member_id: payFor.member_id, class_name: payFor.class_name, date: payFor.date, time: payFor.time, amount: amt, category: payFor.category });
      toast.success(`${payFor.member_name} LUNAS · +${res.points_earned} pts`);
      setPayFor(null); setAmount("");
      await load();
    } catch (e) { toast.error(e.message || "Gagal menandai bayar"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader eyebrow={user.role === "owner" ? "Owner" : "Admin"} title="Pending Payments"
        subtitle="Member yang join kelas mandiri & belum dikonfirmasi bayar. Tekan BAYAR untuk mencatat transaksi (menambah loyalty member)." />

      <div className="mb-4 hsc-label">Belum Bayar ({rows.length})</div>

      {loading ? <Loading /> : !rows.length ? (
        <EmptyState icon={CheckCircle2} title="Tidak ada pembayaran tertunda" subtitle="Semua join kelas mandiri sudah dikonfirmasi." />
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <Card key={rowKey(r)} className="flex flex-wrap items-center justify-between gap-4" data-testid={`pending-row-${r.member_id}`}>
              <div className="min-w-0">
                <div className="text-white">{r.member_name}</div>
                <div className="text-sm text-[#A1A1A6]">{r.class_name} · {r.category}</div>
                <div className="text-[11px] text-[#6E6E73]">{formatDate(r.date)} · {r.time}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => { setPayFor(r); setAmount(""); }} data-testid={`pending-pay-${r.member_id}`}>
                  <DollarSign className="h-4 w-4" /> Bayar
                </Button>
                <Button variant="outline" disabled={msBusy === rowKey(r)} onClick={() => confirmMembership(r)} data-testid={`pending-membership-${r.member_id}`}>
                  <Award className="h-4 w-4" /> {msBusy === rowKey(r) ? "..." : "Membership"}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={!!payFor} onClose={() => (saving ? null : setPayFor(null))} title="Tandai Bayar">
        {payFor && (
          <div className="space-y-4">
            <div className="text-sm text-[#8E8E93]">{payFor.member_name} · {payFor.class_name}</div>
            <Field label="Nominal (Rp)">
              <input className="hsc-input" type="number" inputMode="numeric" min="0" value={amount} autoFocus
                placeholder="mis. 150000" onChange={(e) => setAmount(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }} data-testid="pending-amount" />
              {parseInt(String(amount).replace(/[^0-9]/g, ""), 10) > 0 && (
                <div className="mt-1 text-xs text-[#6E6E73]">{formatIDR(parseInt(String(amount).replace(/[^0-9]/g, ""), 10))}</div>
              )}
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" disabled={saving} onClick={() => setPayFor(null)} data-testid="pending-cancel">Batal</Button>
              <Button disabled={saving} onClick={submit} data-testid="pending-submit">{saving ? "Menyimpan..." : "Simpan & Catat"}</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
